import { Component, ViewChild, ViewContainerRef, OnInit, OnDestroy, HostListener, ComponentRef, Type, Injector, CUSTOM_ELEMENTS_SCHEMA, Input, Output, EventEmitter, signal } from "@angular/core";
import { Subscription } from "rxjs";
import { DialogConfig, DialogRef, DialogService } from "../../../core/services/dialog.service";
import { CommonModule } from "@angular/common";
import { trigger, transition, style, animate, query, animateChild } from "@angular/animations";

@Component({
    selector: 'app-dialog',
    templateUrl: './app-dialog.html',
    styleUrl: './app-dialog.css',
    standalone: true,
    imports: [
        CommonModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    animations: [
        trigger('dialogAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-20px)' }),
                query('@dialogContentAnimation', [animateChild()], { optional: true }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                query('@dialogContentAnimation', [animateChild()], { optional: true }),
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
            ])
        ]),
        trigger('dialogContentAnimation', [
            transition(':enter', [
                style({ opacity: 0, scale: 0 }),
                animate('200ms 100ms ease-out', style({ opacity: 1, scale: 1 })) // delay de 100ms
            ]),
            transition(':leave', [
                animate('200ms ease-out', style({ opacity: 0, scale: 0 }))
            ])
        ])
    ]
})
export class DialogComponent implements OnInit, OnDestroy {
    private _isStatic = false;
    @Input() set isStatic(val: boolean) {
        this._isStatic = val;
        if (val) {
            this.show = this._isOpen;
        }
    }
    get isStatic() { return this._isStatic; }

    @Output() isOpenChange = new EventEmitter<boolean>();

    private _isOpen = false;
    @Input() set isOpen(val: boolean) {
        this._isOpen = val;
        if (this.isStatic) {
            this.show = val;
        }
    }
    get isOpen() { return this._isOpen; }

    @Input() title?: string;
    @Input() description?: string;
    @Input() icon?: string;
    @Input() closable?: boolean;
    @Input() width: string = '500px';

    show = false;
    config: DialogConfig = {};
    private sub?: Subscription;

    get currentTitle() { return this.title ?? this.config.title; }
    get currentDescription() { return this.description ?? this.config.description; }
    get currentIcon() { return this.icon ?? this.config.icon; }
    get currentClosable() { return this.closable !== undefined ? this.closable : (this.config.closable ?? true); }
    get currentWidth() { return this.width !== '500px' ? this.width : (this.config.width ?? '500px'); }

    dialogs = signal<any[]>([]);

    constructor(public dialogService: DialogService, private injector: Injector) { }

    ngOnInit() {
        if (this.isStatic) {
            this.show = this.isOpen;
            return;
        }

        this.sub = this.dialogService.dialogs$.subscribe((instances: any[]) => {
            if (instances.length === 0) {
                this.show = false;
                this.dialogs.set([]);
            } else {
                this.show = true;
                const mapped = instances.map((inst, index) => {
                    const customInjector = Injector.create({
                        providers: [{ provide: DialogRef, useValue: inst.ref }],
                        parent: this.injector
                    });

                    return {
                        id: inst.id,
                        component: inst.component,
                        config: inst.config,
                        injector: customInjector,
                        currentTitle: inst.config.title,
                        currentDescription: inst.config.description,
                        currentIcon: inst.config.icon,
                        currentClosable: inst.config.closable ?? true,
                        widthStyle: this.getWidthStyle(inst.config),
                        zIndex: 500 + index * 10
                    };
                });
                this.dialogs.set(mapped);
            }
        });
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
    }


    close() {
        if (this.isStatic) {
            this.isOpenChange.emit(false);
            this.show = false;
        } else {
            this.dialogService.close();
        }
    }

    closeDialog(id: string) {
        this.dialogService.closeById(id);
    }

    @HostListener('document:keydown.escape')
    onEsc() {
        if (this.isStatic) {
            if (this.currentClosable) this.close();
        } else {
            const currentDialogs = this.dialogs();
            if (currentDialogs.length > 0) {
                const topDialog = currentDialogs[currentDialogs.length - 1];
                if (topDialog.currentClosable) {
                    this.closeDialog(topDialog.id);
                }
            }
        }
    }

    get widthStyle() {
        return this.getWidthStyle(this.config);
    }

    getWidthStyle(config: DialogConfig) {
        const bp = config.breakpoints;
        const currentWidth = config.width ?? '500px';
        if (!bp) return { width: currentWidth };

        const w = window.innerWidth;
        for (const [breakpoint, width] of Object.entries(bp)) {
            if (w <= parseInt(breakpoint)) return { width };
        }
        return { width: currentWidth };
    }
}